import type { $Enums } from '@prisma/client';
import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { deny } from '../types';
import {
  decideHostCharge,
  HOST_CHARGE_FIELDS,
  HOST_CHARGE_OPS,
  type HostChargeFacts,
  type HostChargeField,
  type HostChargeOp,
} from './orderChargeBillLock';

/**
 * The charges window today: `BuySellOrderValidator.isFieldEditable('expenses')` (shared/src/validators/BuySellOrder.ts,
 * `toClosed`). The buy-sell order lane names the same window `BUY_SELL_ORDER_EXPENSES_EDITABLE_STATUSES`
 * (entities/buySellOrder.ts on its stack); keep the two equal until they are deduplicated.
 */
export const BUY_SELL_ORDER_CHARGE_EDITABLE_STATUSES: readonly $Enums.BuySellOrderStatus[] = [
  'DRAFT',
  'SUBMITTED',
  'PAYMENT_RECONCILIATION',
  'CLOSED',
];

export type BuySellOrderExpenseFacts = HostChargeFacts<$Enums.BuySellOrderStatus>;

const statusLock = (status: BuySellOrderExpenseFacts['hostStatus']) =>
  deny(
    'BUY_SELL_ORDER_EXPENSE_CHARGES_LOCKED',
    `Charges are locked once the buy-sell order is ${getStatusLabel(status)}.`
  );

/** Buy-sell order charges: the shared host charge rule over this host's window (`decideHostCharge`). */
export const buySellOrderExpensePolicy: Policy<BuySellOrderExpenseFacts, HostChargeField, HostChargeOp> = definePolicy<
  BuySellOrderExpenseFacts,
  HostChargeField,
  HostChargeOp
>({
  kind: 'buy-sell order charge',
  fields: HOST_CHARGE_FIELDS,
  ops: HOST_CHARGE_OPS,
  requires: {},
  entity: (facts, action) =>
    decideHostCharge(
      { editableStatuses: BUY_SELL_ORDER_CHARGE_EDITABLE_STATUSES, statusLock: statusLock(facts.hostStatus) },
      facts,
      action
    ),
});
